import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Capacitor } from '@capacitor/core';
import { NativeLiquidGlass } from 'src/app/plugins/native-runtime.plugins';
import { HapticLanguageService } from 'src/app/services/ui/haptic-language.service';

export type SecureOperationPhase = 'idle' | 'processing' | 'success' | 'error';

export interface SecureOperationFeedbackState {
  visible: boolean;
  phase: SecureOperationPhase;
  label: string;
  native: boolean;
  animationId: number;
}

@Injectable({ providedIn: 'root' })
export class SecureOperationFeedbackService {
  private readonly stateSubject = new BehaviorSubject<SecureOperationFeedbackState>({
    visible: false,
    phase: 'idle',
    label: '',
    native: Capacitor.getPlatform() === 'ios',
    animationId: 0,
  });
  private animationSequence = 0;

  readonly state$ = this.stateSubject.asObservable();

  constructor(private readonly haptics: HapticLanguageService) {
    if (Capacitor.getPlatform() !== 'ios') return;

    NativeLiquidGlass.addListener('secureOperationAnimationComplete', event => {
      this.animationCompleted(Number(event?.animationId), event?.phase);
    }).catch(() => undefined);
  }

  begin(label = 'Generando código dinámico'): void {
    this.publish(true, 'processing', label);
  }

  succeed(label = 'Operación autorizada', visibleForMs = 1900): void {
    void this.haptics.success();
    this.finish('success', label, visibleForMs);
  }

  succeedIfCurrent(expectedLabel: string, label: string, visibleForMs = 1900): void {
    const current = this.stateSubject.value;
    if (!current.visible || current.phase !== 'processing' || current.label !== expectedLabel) return;
    this.succeed(label, visibleForMs);
  }

  fail(label = 'No se pudo autorizar', visibleForMs = 1600): void {
    void this.haptics.error();
    this.finish('error', label, visibleForMs);
  }

  hide(): void {
    const animationId = ++this.animationSequence;
    this.stateSubject.next({ visible: false, phase: 'idle', label: '', native: false, animationId });
    if (Capacitor.getPlatform() === 'ios') {
      NativeLiquidGlass.setRegion({ region: 'secure-operation', visible: false }).catch(() => undefined);
    }
  }

  /** Called only by the renderer after its final visual animation really ends. */
  animationCompleted(animationId: number, phase?: SecureOperationPhase): void {
    const current = this.stateSubject.value;
    if (!Number.isFinite(animationId) || animationId !== current.animationId) return;
    if (!current.visible || (current.phase !== 'success' && current.phase !== 'error')) return;
    if (phase && phase !== current.phase) return;
    this.hide();
  }

  private finish(phase: 'success' | 'error', label: string, visibleForMs: number): void {
    // Kept in the public API for backwards compatibility. Rendering completion,
    // rather than a guessed timeout, is now the only authority that dismisses it.
    void visibleForMs;
    this.publish(true, phase, label);
  }

  private publish(visible: boolean, phase: SecureOperationPhase, label: string): void {
    const native = Capacitor.getPlatform() === 'ios';
    const animationId = ++this.animationSequence;
    this.stateSubject.next({ visible, phase, label, native, animationId });
    if (!native) return;

    NativeLiquidGlass.setRegion({ region: 'secure-operation', visible, phase, label, animationId })
      .then(result => {
        if (result?.available) return;
        const current = this.stateSubject.value;
        if (current.animationId === animationId) {
          this.stateSubject.next({ ...current, native: false });
        }
      })
      .catch(() => {
        const current = this.stateSubject.value;
        if (current.animationId === animationId) {
          this.stateSubject.next({ ...current, native: false });
        }
      });
  }
}
